<template>
  <div class="users-page">
    <section class="users-hero">
      <div class="label">
        <div>
          <span class="eyebrow">Administration</span>
          <h4>User Management</h4>
        </div>
        <button class="btn-refresh" @click="fetchAll()">Refresh</button>
      </div>
      <p class="intro">
        Review accounts, confirmation status and access levels. Resend confirmation emails,
        manually activate accounts, and manage admin / god roles. Role changes are enforced
        server-side &mdash; god access can only be granted by another god user.
      </p>
    </section>

    <div v-if="error" class="alert">{{ error }}</div>
    <div v-if="notice" class="notice">{{ notice }}</div>

    <!-- Summary cards -->
    <div class="kpi-row">
      <div v-for="card in summaryCards" :key="card.key" class="kpi-card" :class="card.tone ? 'tone-' + card.tone : ''">
        <span class="kpi-label">{{ card.label }}</span>
        <span class="kpi-value">{{ card.value }}</span>
      </div>
    </div>

    <!-- Registration trend -->
    <section class="registration-panel">
      <div class="registration-head">
        <div class="registration-heading-group">
          <div>
            <span class="eyebrow">{{ registrationDays }} day trend</span>
            <h5>New user registrations</h5>
            <p>Accounts created each day</p>
          </div>
          <div class="registration-total">
            <strong>{{ registrationTotal }}</strong>
            <span>users registered</span>
          </div>
        </div>
        <div class="chart-window-toggle" aria-label="Registration chart window">
          <button
            v-for="days in registrationDayOptions"
            :key="'registration-days-' + days"
            type="button"
            :class="{ active: registrationDays === days }"
            :disabled="registrationLoading && registrationDays === days"
            @click="setRegistrationDays(days)"
          >{{ days }}d</button>
        </div>
      </div>

      <div v-if="registrationLoading && !registrationSeries.length" class="registration-empty">
        Loading registration history...
      </div>
      <div v-else-if="registrationSeries.length" class="registration-chart-wrap">
        <svg
          class="registration-chart"
          viewBox="0 0 720 180"
          role="img"
          :aria-label="`Daily user registrations for the last ${registrationDays} days`"
        >
          <g class="registration-grid">
            <g v-for="line in registrationGridLines" :key="'registration-grid-' + line.value">
              <line x1="44" :y1="line.y" x2="708" :y2="line.y"></line>
              <text x="35" :y="line.y + 4">{{ line.value }}</text>
            </g>
          </g>
          <g class="registration-bars">
            <g v-for="bar in registrationChartBars" :key="bar.key">
              <rect
                :x="bar.x"
                :y="bar.y"
                :width="bar.width"
                :height="bar.height"
                rx="3"
                tabindex="0"
              >
                <title>{{ bar.title }}</title>
              </rect>
              <circle
                v-if="bar.count === 0"
                class="registration-zero"
                :cx="bar.x + (bar.width / 2)"
                cy="143"
                r="1.8"
              >
                <title>{{ bar.title }}</title>
              </circle>
              <text
                v-if="bar.count > 0"
                class="registration-value"
                :x="bar.x + (bar.width / 2)"
                :y="bar.y - 6"
              >{{ bar.count }}</text>
            </g>
          </g>
          <g class="registration-axis">
            <text
              v-for="tick in registrationAxisTicks"
              :key="tick.key"
              :x="tick.x"
              y="173"
            >{{ tick.label }}</text>
          </g>
        </svg>
      </div>
      <div v-else class="registration-empty">
        {{ registrationError || 'No registration history is available yet.' }}
      </div>
    </section>

    <!-- Filters -->
    <div class="filters">
      <input
        class="hfilter grow"
        type="text"
        v-model="filters.q"
        placeholder="Search id, username, email or name"
        @input="onSearchInput"
        @keyup.enter="applyFilters"
      />
      <AdminSelect v-model="filters.status" :options="statusOptions" label="Account status" @change="applyFilters" />
      <AdminSelect v-model="filters.role" :options="roleOptions" label="Account role" @change="applyFilters" />
      <AdminSelect v-model="filters.provider" :options="providerOptions" label="Sign-in provider" @change="applyFilters" />
      <a v-if="hasActiveFilters" class="focus-clear" @click="clearFilters">clear</a>
      <!-- Off by default: the email toggles only appear when asked for. -->
      <AdminSelect class="view-filter" v-model="view" :options="viewOptions" label="Table columns" prefix="Show: " @change="fetchUsers" />
    </div>

    <!-- Table -->
    <AdminTable
      class="users-admin-table"
      :columns="tableColumns"
      :rows="users"
      :row-key="userId"
      :loading="loading"
      max-height="50vh"
      :min-width="view === 'notifications' ? '860px' : '1180px'"
      empty-text="No users match these filters."
      clickable
      @row-click="openDetail"
    >
      <template v-slot:cell-id="{ row }">
        <span class="muted-sub mono">#{{ userId(row) }}</span>
      </template>
      <template v-slot:cell-username="{ row }">
        <span class="user-name" :title="username(row) || ''">{{ username(row) || '—' }}</span>
      </template>
      <template v-slot:cell-email="{ row }">
        <span class="email-cell" :title="email(row) || ''">{{ email(row) || '—' }}</span>
      </template>
      <template v-slot:cell-name="{ row }">
        <span class="text-cell" :title="fullName(row) || ''">{{ fullName(row) || '—' }}</span>
      </template>
      <template v-slot:cell-status="{ row }">
        <span class="status-pill" :class="isActive(row) ? 'st-active' : 'st-pending'">
          {{ isActive(row) ? 'Active' : 'Pending' }}
        </span>
      </template>
      <template v-slot:cell-provider="{ row }">
        <span class="provider-pill" :class="'pv-' + provider(row)">{{ providerLabel(row) }}</span>
      </template>
      <template v-slot:cell-role="{ row }">
        <span class="role-pill" :class="'role-' + role(row)">{{ roleLabel(row) }}</span>
      </template>
      <template v-slot:cell-created="{ row }">
        <div class="time-stack">
          <span class="time-row" :title="fmtTime(createdAt(row))">
            <span class="time-label">Created</span>
            <span class="time-value">{{ fmtDateTimeShort(createdAt(row)) }}</span>
          </span>
          <span class="time-row time-row-muted" :title="fmtTime(updatedAt(row))">
            <span class="time-label">Updated</span>
            <span class="time-value">{{ fmtDateTimeShort(updatedAt(row)) }}</span>
          </span>
        </div>
      </template>
      <template v-slot:cell="{ row, column, value }">
        <template v-if="column.notificationType">
          <ToggleSwitch
            v-if="offers(row, column.notificationType)"
            :value="row.notifications[column.notificationType.key] === true"
            :busy="isBusy(row, 'notif:' + column.notificationType.key)"
            :label="`${column.notificationType.label} for ${username(row) || email(row)}`"
            @change="toggleNotification(row, column.notificationType, $event)" />
          <span v-else class="muted-sub" title="Only offered to admins">—</span>
        </template>
        <span v-else class="gc-cell-text">{{ value }}</span>
      </template>
      <template v-slot:cell-actions="{ row }">
        <AdminActionMenu>
          <button class="row-btn" @click.stop="openDetail(row)">Details</button>
          <button
            v-if="canResend(row)"
            class="row-btn"
            :disabled="isBusy(row, 'resend')"
            @click.stop="resendConfirmation(row)"
          >{{ isBusy(row, 'resend') ? 'Sending...' : 'Resend' }}</button>
          <button
            v-if="!isActive(row)"
            class="row-btn apply"
            :disabled="isBusy(row, 'activate')"
            @click.stop="activateUser(row)"
          >{{ isBusy(row, 'activate') ? 'Activating...' : 'Activate' }}</button>
          <button
            class="row-btn"
            :class="{ on: isAdmin(row) }"
            :disabled="isBusy(row, 'role')"
            @click.stop="toggleAdmin(row)"
          >{{ isAdmin(row) ? 'Revoke admin' : 'Make admin' }}</button>
          <button
            v-if="isGod"
            class="row-btn god"
            :class="{ on: isGodUser(row) }"
            :disabled="isBusy(row, 'role')"
            @click.stop="toggleGod(row)"
          >{{ isGodUser(row) ? 'Revoke god' : 'Make god' }}</button>
        </AdminActionMenu>
      </template>
    </AdminTable>

    <div v-if="false" class="table-wrap">
      <table class="h-table">
        <thead>
          <tr>
            <th>ID</th><th>Username</th><th>Email</th><th>Name</th>
            <th>Status</th><th>Provider</th><th>Role</th><th>Created / Updated</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="u in users" :key="userId(u)" class="user-row" @click="openDetail(u)">
            <td class="muted-sub mono">#{{ userId(u) }}</td>
            <td><span class="user-name">{{ username(u) || '—' }}</span></td>
            <td class="email-cell">{{ email(u) || '—' }}</td>
            <td>{{ fullName(u) || '—' }}</td>
            <td>
              <span class="status-pill" :class="isActive(u) ? 'st-active' : 'st-pending'">
                {{ isActive(u) ? 'Active' : 'Pending' }}
              </span>
            </td>
            <td>
              <span class="provider-pill" :class="'pv-' + provider(u)">{{ providerLabel(u) }}</span>
            </td>
            <td>
              <span class="role-pill" :class="'role-' + role(u)">{{ roleLabel(u) }}</span>
            </td>
            <td class="when-cell">
              <div class="time-stack">
                <span class="time-row" :title="fmtTime(createdAt(u))">
                  <span class="time-label">Created</span>
                  <span class="time-value">{{ fmtDateTimeShort(createdAt(u)) }}</span>
                </span>
                <span class="time-row time-row-muted" :title="fmtTime(updatedAt(u))">
                  <span class="time-label">Updated</span>
                  <span class="time-value">{{ fmtDateTimeShort(updatedAt(u)) }}</span>
                </span>
              </div>
            </td>
            <td class="u-actions">
              <button class="row-btn" @click.stop="openDetail(u)">Details</button>
              <button
                v-if="canResend(u)"
                class="row-btn"
                :disabled="isBusy(u, 'resend')"
                @click.stop="resendConfirmation(u)"
              >{{ isBusy(u, 'resend') ? 'Sending…' : 'Resend' }}</button>
              <button
                v-if="!isActive(u)"
                class="row-btn apply"
                :disabled="isBusy(u, 'activate')"
                @click.stop="activateUser(u)"
              >{{ isBusy(u, 'activate') ? 'Activating…' : 'Activate' }}</button>
              <button
                class="row-btn"
                :class="{ on: isAdmin(u) }"
                :disabled="isBusy(u, 'role')"
                @click.stop="toggleAdmin(u)"
              >{{ isAdmin(u) ? 'Revoke admin' : 'Make admin' }}</button>
              <button
                v-if="isGod"
                class="row-btn god"
                :class="{ on: isGodUser(u) }"
                :disabled="isBusy(u, 'role')"
                @click.stop="toggleGod(u)"
              >{{ isGodUser(u) ? 'Revoke god' : 'Make god' }}</button>
            </td>
          </tr>
          <tr v-if="!users.length && !loading">
            <td colspan="9" class="empty">No users match these filters.</td>
          </tr>
          <tr v-if="loading">
            <td colspan="9" class="empty">Loading users…</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="pager" v-if="total > 0">
      <span class="pager-info">Showing {{ showingFrom }}–{{ showingTo }} of {{ total }}</span>
      <div class="pager-buttons">
        <button class="row-btn" :disabled="!hasPrev || loading" @click="prevPage">Previous</button>
        <button class="row-btn" :disabled="!hasNext || loading" @click="nextPage">Next</button>
      </div>
    </div>

    <!-- Detail drawer -->
    <div v-if="selectedUser" class="user-drawer" @click.self="closeDetail">
      <aside class="user-panel">
        <div class="user-panel-head">
          <div>
            <span class="role-pill" :class="'role-' + role(selectedUser)">{{ roleLabel(selectedUser) }}</span>
            <h5>{{ username(selectedUser) || fullName(selectedUser) || ('User #' + userId(selectedUser)) }}</h5>
            <span class="muted-sub">User #{{ userId(selectedUser) }}</span>
          </div>
          <button class="panel-close" @click="closeDetail">x</button>
        </div>

        <div v-if="selectedUserLoading" class="panel-loading">Loading user details…</div>
        <div v-if="selectedUserError" class="panel-error">{{ selectedUserError }}</div>

        <div class="user-metrics">
          <div>
            <span class="metric-label">Status</span>
            <strong :class="isActive(selectedUser) ? 'metric-ok' : 'metric-warn'">
              {{ isActive(selectedUser) ? 'Active' : 'Pending' }}
            </strong>
          </div>
          <div>
            <span class="metric-label">Provider</span>
            <strong>{{ providerLabel(selectedUser) }}</strong>
          </div>
          <div>
            <span class="metric-label">Builds</span>
            <strong>{{ buildCount(selectedUser) }}</strong>
          </div>
        </div>

        <div class="user-panel-section">
          <span class="section-kicker">Account</span>
          <div class="user-spec-grid">
            <div class="user-spec-row"><span>Username</span><strong>{{ username(selectedUser) || '—' }}</strong></div>
            <div class="user-spec-row"><span>Email</span><strong>{{ email(selectedUser) || '—' }}</strong></div>
            <div class="user-spec-row"><span>Name</span><strong>{{ fullName(selectedUser) || '—' }}</strong></div>
            <div class="user-spec-row"><span>Sign-in methods</span><strong>{{ providerLabel(selectedUser) }}</strong></div>
            <div class="user-spec-row"><span>Google linked</span><strong>{{ googleLinked(selectedUser) ? 'Yes' : 'No' }}</strong></div>
            <div class="user-spec-row"><span>Has password</span><strong>{{ hasPassword(selectedUser) ? 'Yes' : 'No' }}</strong></div>
            <div class="user-spec-row"><span>Admin</span><strong>{{ isAdmin(selectedUser) ? 'Yes' : 'No' }}</strong></div>
            <div class="user-spec-row"><span>God</span><strong>{{ isGodUser(selectedUser) ? 'Yes' : 'No' }}</strong></div>
            <div class="user-spec-row"><span>Created</span><strong>{{ fmtTime(createdAt(selectedUser)) }}</strong></div>
            <div class="user-spec-row"><span>Updated</span><strong>{{ fmtTime(updatedAt(selectedUser)) }}</strong></div>
          </div>
        </div>

        <div class="user-panel-actions">
          <button
            v-if="canResend(selectedUser)"
            class="gc-act"
            :disabled="isBusy(selectedUser, 'resend')"
            @click="resendConfirmation(selectedUser)"
          >{{ isBusy(selectedUser, 'resend') ? 'Sending…' : 'Resend confirmation' }}</button>
          <button
            v-if="!isActive(selectedUser)"
            class="gc-act primary"
            :disabled="isBusy(selectedUser, 'activate')"
            @click="activateUser(selectedUser)"
          >{{ isBusy(selectedUser, 'activate') ? 'Activating…' : 'Activate account' }}</button>
          <button
            class="gc-act"
            :disabled="isBusy(selectedUser, 'role')"
            @click="toggleAdmin(selectedUser)"
          >{{ isAdmin(selectedUser) ? 'Revoke admin' : 'Make admin' }}</button>
          <button
            v-if="isGod"
            class="gc-act"
            :disabled="isBusy(selectedUser, 'role')"
            @click="toggleGod(selectedUser)"
          >{{ isGodUser(selectedUser) ? 'Revoke god' : 'Make god' }}</button>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import { apiDomain } from '../../config'
import AdminActionMenu from './shared/AdminActionMenu.vue'
import AdminTable from './shared/AdminTable.vue'
import ToggleSwitch from './shared/ToggleSwitch.vue'
import AdminSelect from './shared/AdminSelect.vue'

export default {
  name: 'UserManagement',
  components: {
    AdminActionMenu,
    AdminTable,
    ToggleSwitch,
    AdminSelect
  },
  data () {
    return {
      summary: null,
      users: [],
      total: 0,
      limit: 25,
      offset: 0,
      filters: { q: '', status: 'all', role: 'all', provider: 'all' },
      statusOptions: [
        { value: 'all', label: 'Any status' },
        { value: 'active', label: 'Active' },
        { value: 'pending', label: 'Pending confirmation' },
      ],
      roleOptions: [
        { value: 'all', label: 'Any role' },
        { value: 'user', label: 'User' },
        { value: 'admin', label: 'Admin' },
        { value: 'god', label: 'God' },
      ],
      providerOptions: [
        { value: 'all', label: 'Any provider' },
        { value: 'password', label: 'Password' },
        { value: 'google', label: 'Google' },
      ],
      viewOptions: [
        { value: 'details', label: 'Account details' },
        { value: 'notifications', label: 'Email notifications' },
      ],
      // 'details' (default) or 'notifications': which columns the table shows.
      view: 'details',
      notificationTypes: [],
      loading: false,
      error: null,
      notice: null,
      busy: {},
      selectedUser: null,
      selectedUserLoading: false,
      selectedUserError: '',
      searchTimer: null,
      registrationSeries: [],
      registrationDays: 30,
      registrationDayOptions: [7, 14, 30],
      registrationLoading: true,
      registrationError: ''
    }
  },
  computed: {
    ...mapGetters(['getUser']),
    // God-only controls (e.g. the god toggle) are gated on the *current* user.
    // The backend is the real authority; this just hides controls that would 403.
    isGod () {
      return this.truthy(this.pick(this.getUser, ['is_god', 'isGod', 'god'], false))
    },
    summaryCards () {
      const s = this.summary || {}
      return [
        { key: 'total', label: 'Total users', value: this.numOr(s, ['total', 'totalUsers', 'count']) },
        { key: 'active', label: 'Active', value: this.numOr(s, ['active', 'activeUsers']), tone: 'ok' },
        { key: 'pending', label: 'Pending confirmation', value: this.numOr(s, ['pendingConfirmation', 'pending', 'inactive']), tone: 'warn' },
        { key: 'admins', label: 'Admins', value: this.numOr(s, ['admins', 'adminCount']) },
        { key: 'google', label: 'Google linked', value: this.numOr(s, ['googleLinked', 'google', 'googleUsers']) }
      ]
    },
    registrationTotal () {
      return this.registrationSeries.reduce((total, row) => {
        return total + this.registrationCount(row)
      }, 0)
    },
    registrationChartMax () {
      const max = Math.max.apply(null, this.registrationSeries.map(row => this.registrationCount(row)).concat([1]))
      if (max <= 5) return max
      const magnitude = Math.pow(10, Math.floor(Math.log10(max)))
      return Math.ceil(max / magnitude) * magnitude
    },
    registrationGridLines () {
      const max = this.registrationChartMax
      const lines = []
      for (let index = 0; index <= 4; index += 1) {
        const value = Math.round((max * index) / 4)
        if (lines.some(line => line.value === value)) continue
        lines.push({
          value,
          y: 143 - ((value / max) * 116)
        })
      }
      return lines
    },
    registrationChartBars () {
      const rows = this.registrationSeries
      if (!rows.length) return []
      const plotWidth = 664
      const band = plotWidth / rows.length
      const width = Math.max(5, Math.min(28, band * 0.72))
      return rows.map((row, index) => {
        const count = this.registrationCount(row)
        const height = count ? Math.max(3, (count / this.registrationChartMax) * 116) : 0
        return {
          key: `registration-bar-${index}`,
          x: 44 + (index * band) + ((band - width) / 2),
          y: 143 - height,
          width,
          height,
          count,
          title: `${this.registrationFullDateLabel(row)}: ${count} user${count === 1 ? '' : 's'}`
        }
      })
    },
    registrationAxisTicks () {
      const rows = this.registrationSeries
      if (!rows.length) return []
      const tickCount = Math.min(6, rows.length)
      const seen = {}
      const ticks = []
      for (let index = 0; index < tickCount; index += 1) {
        const rowIndex = tickCount === 1
          ? 0
          : Math.round((index * (rows.length - 1)) / (tickCount - 1))
        if (seen[rowIndex]) continue
        seen[rowIndex] = true
        ticks.push({
          key: `registration-tick-${rowIndex}`,
          x: 44 + (((rowIndex + 0.5) / rows.length) * 664),
          label: this.registrationDateLabel(rows[rowIndex])
        })
      }
      return ticks
    },
    hasActiveFilters () {
      const f = this.filters
      return !!(f.q || f.status !== 'all' || f.role !== 'all' || f.provider !== 'all')
    },
    tableColumns () {
      return this.view === 'notifications' ? this.notificationColumns : this.userColumns
    },
    // Who gets which email, one toggle column per type (config/notificationTypes.js).
    notificationColumns () {
      return [
        { key: 'id', label: 'ID', width: '56px', nowrap: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => Number(this.userId(r)) || this.userId(r) },
        { key: 'username', label: 'Username', width: '170px', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => this.username(r) },
        { key: 'email', label: 'Email', width: '220px', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => this.email(r) },
        { key: 'role', label: 'Role', width: '96px', nowrap: true, sortable: true, sortAccessor: (r) => this.role(r) },
        ...this.notificationTypes.map((type) => ({
          key: `notif_${type.key}`,
          label: type.label,
          width: '150px',
          align: 'center',
          nowrap: true,
          notificationType: type,
          sortable: true,
          sortAccessor: (r) => (r.notifications && r.notifications[type.key] ? 1 : 0)
        }))
      ]
    },
    userColumns () {
      return [
        { key: 'id', label: 'ID', width: '56px', nowrap: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => Number(this.userId(r)) || this.userId(r) },
        { key: 'username', label: 'Username', width: '180px', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => this.username(r) },
        { key: 'email', label: 'Email', width: '230px', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => this.email(r) },
        { key: 'name', label: 'Name', width: '190px', breakable: true, sortable: true, defaultDir: 'asc', sortAccessor: (r) => this.fullName(r) },
        { key: 'status', label: 'Status', width: '116px', nowrap: true, sortable: true, sortAccessor: (r) => (this.isActive(r) ? 1 : 0) },
        { key: 'provider', label: 'Provider', width: '128px', nowrap: true, sortable: true, sortAccessor: (r) => this.providerLabel(r) },
        { key: 'role', label: 'Role', width: '96px', nowrap: true, sortable: true, sortAccessor: (r) => this.role(r) },
        { key: 'created', label: 'Created / Updated', width: '190px', nowrap: true, sortable: true, sortAccessor: (r) => new Date(this.createdAt(r)).getTime() || 0 },
        { key: 'actions', label: '', width: '118px', align: 'right', nowrap: true }
      ]
    },
    showingFrom () {
      return this.total === 0 ? 0 : this.offset + 1
    },
    showingTo () {
      return Math.min(this.offset + this.limit, this.total)
    },
    hasPrev () {
      return this.offset > 0
    },
    hasNext () {
      return this.offset + this.limit < this.total
    }
  },
  mounted () {
    this.fetchAll()
  },
  beforeDestroy () {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    buildQuery () {
      const f = this.filters
      const p = new URLSearchParams()
      if (f.q) p.set('q', f.q.trim())
      p.set('status', f.status || 'all')
      p.set('role', f.role || 'all')
      p.set('provider', f.provider || 'all')
      p.set('limit', String(this.limit))
      p.set('offset', String(this.offset))
      if (this.view === 'notifications') p.set('include', 'notifications')
      return p.toString()
    },
    async fetchAll () {
      await Promise.all([this.fetchSummary(), this.fetchUsers(), this.fetchRegistrations()])
    },
    async fetchRegistrations () {
      this.registrationLoading = true
      this.registrationError = ''
      try {
        const days = Math.max(1, Math.min(30, Number(this.registrationDays) || 30))
        const { data } = await axios.get(`${apiDomain}admin/audience/daily?days=${days}`, { headers: this.authHeaders() })
        this.registrationSeries = this.asList(data, ['series', 'data']).slice().sort((a, b) => {
          return this.registrationDateValue(a) - this.registrationDateValue(b)
        })
      } catch (e) {
        this.registrationSeries = []
        this.registrationError = this.errMsg(e)
      } finally {
        this.registrationLoading = false
      }
    },
    setRegistrationDays (days) {
      const next = Number(days) || 30
      if (next === this.registrationDays) return
      this.registrationDays = next
      this.fetchRegistrations()
    },
    registrationCount (row) {
      return Math.max(0, Number(this.pick(row, ['registrations', 'registration_count', 'registrationCount'], 0)) || 0)
    },
    registrationDateValue (row) {
      const value = this.pick(row, ['stat_date', 'statDate', 'date'], '')
      if (!value) return 0
      const date = new Date(String(value).slice(0, 10) + 'T00:00:00')
      return isNaN(date.getTime()) ? 0 : date.getTime()
    },
    registrationDateLabel (row) {
      const timestamp = this.registrationDateValue(row)
      if (!timestamp) return ''
      return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    },
    registrationFullDateLabel (row) {
      const timestamp = this.registrationDateValue(row)
      if (!timestamp) return 'Unknown date'
      return new Date(timestamp).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },
    async fetchSummary () {
      try {
        const { data } = await axios.get(`${apiDomain}admin/users/summary`, { headers: this.authHeaders() })
        this.summary = this.unwrap(data, ['summary', 'data']) || data
      } catch (e) {
        this.error = this.errMsg(e)
      }
    },
    async fetchUsers () {
      this.loading = true
      try {
        const { data } = await axios.get(`${apiDomain}admin/users?${this.buildQuery()}`, { headers: this.authHeaders() })
        this.users = this.asList(data, ['users', 'items', 'data', 'results'])
        if (Array.isArray(data && data.notificationTypes)) this.notificationTypes = data.notificationTypes
        const total = this.pick(data, ['total', 'count'], null)
        this.total = total !== null ? Number(total) : this.users.length
        if (typeof this.pick(data, ['limit'], null) === 'number') this.limit = this.pick(data, ['limit'], this.limit)
        if (typeof this.pick(data, ['offset'], null) === 'number') this.offset = this.pick(data, ['offset'], this.offset)
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.loading = false
      }
    },
    onSearchInput () {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.applyFilters(), 400)
    },
    applyFilters () {
      if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null }
      this.offset = 0
      this.fetchUsers()
    },
    clearFilters () {
      this.filters = { q: '', status: 'all', role: 'all', provider: 'all' }
      this.applyFilters()
    },
    prevPage () {
      if (!this.hasPrev) return
      this.offset = Math.max(0, this.offset - this.limit)
      this.fetchUsers()
    },
    nextPage () {
      if (!this.hasNext) return
      this.offset += this.limit
      this.fetchUsers()
    },
    openDetail (u) {
      this.selectedUser = u
      this.selectedUserError = ''
      this.fetchDetail(userIdOf(u))
    },
    closeDetail () {
      this.selectedUser = null
      this.selectedUserError = ''
    },
    async fetchDetail (id) {
      if (!id && id !== 0) return
      this.selectedUserLoading = true
      try {
        const { data } = await axios.get(`${apiDomain}admin/users/${encodeURIComponent(id)}`, { headers: this.authHeaders() })
        const detail = this.unwrap(data, ['user', 'data']) || data
        if (this.selectedUser && String(userIdOf(this.selectedUser)) === String(id)) {
          this.selectedUser = Object.assign({}, this.selectedUser, detail)
        }
      } catch (e) {
        this.selectedUserError = this.errMsg(e)
      } finally {
        this.selectedUserLoading = false
      }
    },
    // ---- actions ----
    // A type the API left out of a user's map is not offered to them (admin-only).
    offers (u, type) {
      return !!(u && u.notifications && Object.prototype.hasOwnProperty.call(u.notifications, type.key))
    },
    async toggleNotification (u, type, enabled) {
      const id = userIdOf(u)
      const action = `notif:${type.key}`
      const previous = u.notifications[type.key]
      this.$set(u.notifications, type.key, enabled) // show it now; put it back if saving fails
      this.setBusy(id, action, true)
      try {
        const { data } = await axios.put(
          `${apiDomain}admin/users/${encodeURIComponent(id)}/notifications/${encodeURIComponent(type.key)}`,
          { enabled }, { headers: this.authHeaders() })
        if (data && data.notifications) this.$set(u, 'notifications', data.notifications)
        this.notice = `${type.label} ${enabled ? 'on' : 'off'} for ${this.username(u) || this.email(u)}.`
        this.error = null
      } catch (e) {
        this.$set(u.notifications, type.key, previous)
        this.error = this.errInfo(e).message
      } finally {
        this.setBusy(id, action, false)
      }
    },
    async resendConfirmation (u) {
      const id = userIdOf(u)
      this.setBusy(id, 'resend', true)
      try {
        const { data } = await axios.post(
          `${apiDomain}admin/users/${encodeURIComponent(id)}/resend-confirmation`, {},
          { headers: this.authHeaders() })
        this.notice = this.pick(data, ['message'], 'Confirmation email sent.')
        this.error = null
        await this.refreshUser(id)
      } catch (e) {
        const info = this.errInfo(e)
        // The account became active in the meantime — surface it calmly, not as a hard error.
        if (info.status === 409 && info.code === 'USER_ALREADY_ACTIVE') {
          this.notice = info.message || 'This account is already active.'
          await this.refreshUser(id)
        } else {
          this.error = info.message
        }
      } finally {
        this.setBusy(id, 'resend', false)
      }
    },
    async activateUser (u) {
      const id = userIdOf(u)
      this.setBusy(id, 'activate', true)
      try {
        const { data } = await axios.post(
          `${apiDomain}admin/users/${encodeURIComponent(id)}/activate`, {},
          { headers: this.authHeaders() })
        this.notice = this.pick(data, ['message'], 'Account activated.')
        this.error = null
        await this.refreshUser(id, this.unwrap(data, ['user', 'data']))
      } catch (e) {
        const info = this.errInfo(e)
        if (info.status === 409 && info.code === 'USER_ALREADY_ACTIVE') {
          this.notice = info.message || 'This account is already active.'
          await this.refreshUser(id)
        } else {
          this.error = info.message
        }
      } finally {
        this.setBusy(id, 'activate', false)
      }
    },
    toggleAdmin (u) {
      const target = !this.isAdmin(u)
      if (!window.confirm(`${target ? 'Grant' : 'Revoke'} admin access for ${this.username(u) || ('user #' + userIdOf(u))}?`)) return
      this.changeRole(u, { is_admin: target, is_god: this.isGodUser(u) })
    },
    toggleGod (u) {
      if (!this.isGod) return
      const target = !this.isGodUser(u)
      if (!window.confirm(`${target ? 'Grant' : 'Revoke'} GOD access for ${this.username(u) || ('user #' + userIdOf(u))}?`)) return
      // Granting god implies admin; keep admin set so the account keeps its tooling.
      this.changeRole(u, { is_admin: target ? true : this.isAdmin(u), is_god: target })
    },
    async changeRole (u, body) {
      const id = userIdOf(u)
      this.setBusy(id, 'role', true)
      try {
        const { data } = await axios.post(
          `${apiDomain}admin/users/${encodeURIComponent(id)}/role`, body,
          { headers: this.authHeaders() })
        this.notice = this.pick(data, ['message'], 'Role updated.')
        this.error = null
        await this.refreshUser(id, this.unwrap(data, ['user', 'data']))
      } catch (e) {
        // 403 from the role endpoint typically means a non-god tried to change god,
        // or the caller lost admin access. Show the server message verbatim.
        this.error = this.errMsg(e)
      } finally {
        this.setBusy(id, 'role', false)
      }
    },
    // Replace the row (and open drawer) with a fresh copy. Uses the inline user
    // from a mutation response when present, otherwise re-fetches the detail.
    async refreshUser (id, inlineUser) {
      this.fetchSummary()
      if (inlineUser && typeof inlineUser === 'object') {
        this.replaceUserRow(id, inlineUser)
      } else {
        await this.fetchUsers()
      }
      if (this.selectedUser && String(userIdOf(this.selectedUser)) === String(id)) {
        await this.fetchDetail(id)
      }
    },
    replaceUserRow (id, user) {
      const idx = this.users.findIndex((u) => String(userIdOf(u)) === String(id))
      if (idx >= 0) {
        const next = this.users.slice()
        this.$set(next, idx, Object.assign({}, next[idx], user))
        this.users = next
      }
      if (this.selectedUser && String(userIdOf(this.selectedUser)) === String(id)) {
        this.selectedUser = Object.assign({}, this.selectedUser, user)
      }
    },
    setBusy (id, action, val) {
      this.$set(this.busy, `${id}:${action}`, val)
    },
    isBusy (u, action) {
      return !!this.busy[`${userIdOf(u)}:${action}`]
    },
    // ---- accessors (tolerant to backend field naming) ----
    userId (u) { return userIdOf(u) },
    username (u) { return this.pick(u, ['username', 'user_name', 'handle'], '') },
    email (u) { return this.pick(u, ['email', 'email_address'], '') },
    fullName (u) {
      const direct = this.pick(u, ['name', 'full_name', 'fullName', 'display_name'], '')
      if (direct) return direct
      const first = this.pick(u, ['first_name', 'firstName', 'given_name'], '')
      const last = this.pick(u, ['last_name', 'lastName', 'family_name'], '')
      return [first, last].filter(Boolean).join(' ')
    },
    isActive (u) {
      const v = this.pick(u, ['active', 'is_active', 'isActive', 'confirmed', 'email_confirmed', 'is_confirmed'], null)
      if (v === null) {
        // No explicit flag: treat the presence of a confirmation token as "pending".
        const token = this.pick(u, ['confirmation_token', 'confirmationToken', 'activation_token'], null)
        return !token
      }
      return this.truthy(v)
    },
    isAdmin (u) { return this.truthy(this.pick(u, ['is_admin', 'isAdmin', 'admin'], false)) },
    isGodUser (u) { return this.truthy(this.pick(u, ['is_god', 'isGod', 'god'], false)) },
    role (u) {
      if (this.isGodUser(u)) return 'god'
      if (this.isAdmin(u)) return 'admin'
      return 'user'
    },
    roleLabel (u) {
      return { god: 'God', admin: 'Admin', user: 'User' }[this.role(u)]
    },
    // Provider reflects current capabilities, not signup origin:
    //   password | google | both (= password+google).
    provider (u) {
      const p = String(this.pick(u, ['auth_provider', 'authProvider', 'provider', 'login_provider'], '')).toLowerCase()
      if (p) {
        const hasGoogle = p.indexOf('google') >= 0
        const hasPassword = p.indexOf('password') >= 0
        if (hasGoogle && hasPassword) return 'both'
        if (hasGoogle) return 'google'
        if (hasPassword) return 'password'
      }
      // Fall back to the capability flags / linkage when auth_provider is absent.
      const google = this.googleLinked(u)
      const password = this.hasPassword(u)
      if (google && password) return 'both'
      if (google) return 'google'
      return 'password'
    },
    providerLabel (u) {
      return { google: 'Google', password: 'Password', both: 'Password + Google' }[this.provider(u)]
    },
    googleLinked (u) {
      const flag = this.pick(u, ['google_linked', 'googleLinked'], null)
      if (flag !== null) return this.truthy(flag)
      return !!this.pick(u, ['google_id', 'googleId', 'google_sub', 'sub'], null)
    },
    hasPassword (u) {
      const flag = this.pick(u, ['has_password', 'hasPassword'], null)
      if (flag !== null) return this.truthy(flag)
      return String(this.pick(u, ['auth_provider', 'authProvider', 'provider'], '')).toLowerCase().indexOf('password') >= 0
    },
    canResend (u) {
      // Trust the server flag when present; otherwise fall back to "inactive".
      const flag = this.pick(u, ['can_resend_confirmation', 'canResendConfirmation'], null)
      if (flag !== null) return this.truthy(flag)
      return !this.isActive(u)
    },
    buildCount (u) {
      return this.numOr(u, ['buildCount', 'build_count', 'builds'], 0)
    },
    createdAt (u) { return this.pick(u, ['created_at', 'createdAt', 'created', 'date_created'], null) },
    updatedAt (u) { return this.pick(u, ['updated_at', 'updatedAt', 'updated', 'date_updated'], null) },
    // ---- generic helpers ----
    pick (obj, keys, fallback) {
      if (!obj) return fallback
      for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null) return obj[k]
      }
      return fallback
    },
    unwrap (data, keys) {
      for (const k of keys) {
        if (data && data[k] !== undefined && data[k] !== null) return data[k]
      }
      return null
    },
    asList (data, keys) {
      if (Array.isArray(data)) return data
      const v = this.unwrap(data, keys)
      return Array.isArray(v) ? v : []
    },
    truthy (v) {
      if (v === true) return true
      if (typeof v === 'number') return v !== 0
      if (typeof v === 'string') return ['1', 'true', 'yes', 'y'].indexOf(v.toLowerCase()) >= 0
      return false
    },
    numOr (obj, keys, fallback = '—') {
      const v = this.pick(obj, keys, null)
      if (v === null) return fallback
      const n = Number(v)
      return isNaN(n) ? fallback : n
    },
    fmtDate (t) {
      if (!t) return '—'
      const d = new Date(t)
      return isNaN(d) ? '—' : d.toLocaleDateString()
    },
    fmtDateTimeShort (t) {
      if (!t) return '—'
      const d = new Date(t)
      if (isNaN(d)) return '—'
      return d.toLocaleString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    },
    fmtTime (t) {
      if (!t) return '—'
      const d = new Date(t)
      return isNaN(d) ? '—' : d.toLocaleString()
    },
    fmtRelative (t) {
      if (!t) return '—'
      const d = new Date(t)
      if (isNaN(d)) return '—'
      const s = Math.round((Date.now() - d.getTime()) / 1000)
      if (s < 0) return this.fmtDate(t)
      if (s < 45) return 'just now'
      const m = Math.round(s / 60)
      if (m < 60) return `${m}m ago`
      const h = Math.round(m / 60)
      if (h < 24) return `${h}h ago`
      const days = Math.round(h / 24)
      if (days < 7) return `${days}d ago`
      return this.fmtDate(t)
    },
    errInfo (e) {
      const res = e && e.response
      const body = res && res.data
      const status = res ? res.status : 0
      const code = (body && (body.code || body.error || (body.error && body.error.code))) || ''
      let message = (body && (body.message || body.error)) || e.message || 'Request failed'
      if (typeof message === 'object') message = JSON.stringify(message)
      if (status === 403 && !/admin|forbidden|access/i.test(String(message))) {
        message = 'Admin access required.'
      }
      return { status, code: String(code || ''), message: String(message) }
    },
    errMsg (e) {
      return this.errInfo(e).message
    }
  }
}

// Module-scoped id reader so template, watchers and helpers agree on identity.
function userIdOf (u) {
  if (!u) return ''
  const keys = ['id', '_id', 'user_id', 'userId', 'uuid']
  for (const k of keys) {
    if (u[k] !== undefined && u[k] !== null) return u[k]
  }
  return ''
}
</script>

<style scoped>
.users-page {
  padding: 4px 8px 48px;
  color: #cfd2da;
}



.label {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 1px solid rgba(67, 72, 87, 0.9);
}

.label h4 {
  margin: 0 0 6px;
  color: #d8dde8;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.intro {
  max-width: 760px;
  margin: 0.6rem 0 1.1rem;
  color: #a4acbc;
  font-size: 14px;
  line-height: 1.55;
}

.alert,
.notice {
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.alert {
  border: 1px solid rgba(232, 79, 106, 0.5);
  background: rgba(90, 34, 48, 0.48);
  color: #f3c6cf;
}

.notice {
  border: 1px solid rgba(25, 151, 198, 0.5);
  background: rgba(20, 54, 70, 0.5);
  color: #aee0f3;
}

/* Summary cards */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  margin: 14px 0 8px;
}

.kpi-card {
  display: grid;
  gap: 4px;
  min-height: 70px;
  padding: 12px 14px;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.08), transparent 38%),
    rgba(14, 18, 27, 0.78);
}

.kpi-label {
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.kpi-value {
  color: #eaf0f7;
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
}

.kpi-card.tone-ok .kpi-value { color: #89dfbd; }
.kpi-card.tone-warn { border-color: rgba(224, 192, 126, 0.36); }
.kpi-card.tone-warn .kpi-value { color: #e0c07e; }

/* Registration chart */
.registration-panel {
  margin: 18px 0;
  padding: 16px 18px 12px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 8px;
  background:
    radial-gradient(circle at 10% 0%, rgba(25, 151, 198, 0.09), transparent 34%),
    rgba(10, 14, 22, 0.72);
}

.registration-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 4px;
}

.registration-heading-group {
  display: flex;
  align-items: center;
  gap: 22px;
}

.registration-head h5 {
  margin: 2px 0 3px;
  color: #e4e9f2;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.03em;
}

.registration-head p {
  margin: 0;
  color: #7f899b;
  font-size: 12px;
}

.registration-total {
  display: grid;
  justify-items: start;
  min-width: 94px;
  padding-left: 20px;
  border-left: 1px solid rgba(67, 72, 87, 0.7);
}

.registration-total strong {
  color: #89dfbd;
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
}

.registration-total span {
  margin-top: 3px;
  color: #7f899b;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

.chart-window-toggle {
  display: inline-flex;
  padding: 3px;
  border: 1px solid rgba(67, 72, 87, 0.78);
  border-radius: 999px;
  background: rgba(8, 11, 17, 0.58);
}

.chart-window-toggle button {
  min-width: 36px;
  height: 24px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #7f899b;
  cursor: pointer;
  font-size: 11px;
  font-weight: 900;
}

.chart-window-toggle button.active {
  background: rgba(25, 151, 198, 0.2);
  color: #45c9f7;
  box-shadow: inset 0 0 0 1px rgba(32, 184, 235, 0.38);
}

.chart-window-toggle button:disabled {
  cursor: default;
  opacity: 0.7;
}

.registration-chart-wrap {
  width: 100%;
  min-height: 180px;
  overflow-x: auto;
}

.registration-chart {
  display: block;
  width: 100%;
  min-width: 620px;
  height: 180px;
  overflow: visible;
}

.registration-grid line {
  stroke: rgba(67, 72, 87, 0.42);
  stroke-width: 1;
}

.registration-grid text,
.registration-axis text {
  fill: #70798b;
  font-size: 9px;
  font-weight: 700;
}

.registration-grid text {
  text-anchor: end;
}

.registration-axis text {
  text-anchor: middle;
}

.registration-bars rect {
  fill: #20b8eb;
  stroke: rgba(89, 215, 255, 0.8);
  stroke-width: 1;
  opacity: 0.92;
  transition: fill 0.14s ease, opacity 0.14s ease;
}

.registration-bars rect:hover,
.registration-bars rect:focus {
  fill: #45c9f7;
  opacity: 1;
  outline: none;
}

.registration-zero {
  fill: rgba(127, 137, 155, 0.38);
}

.registration-value {
  fill: #c7f0ff;
  font-size: 9px;
  font-weight: 800;
  text-anchor: middle;
}

.registration-empty {
  display: grid;
  min-height: 180px;
  place-items: center;
  color: #6f788b;
  font-size: 12px;
}

/* Filters */
.filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 18px 0 12px;
}

.hfilter {
  height: 31px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(10, 13, 20, 0.56);
  color: #cfd2da;
  padding: 0 9px;
  font-size: 12px;
  outline: none;
}

/* The column switch sits at the end of the bar, apart from the filters. */
.view-filter {
  margin-left: auto;
}
.view-filter >>> .admin-select__toggle {
  border-color: rgba(25, 151, 198, 0.55);
}

.hfilter.grow {
  flex: 1;
  min-width: 240px;
}

.hfilter:focus {
  border-color: rgba(25, 151, 198, 0.85);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12);
}

.focus-clear {
  color: #1fb2e8;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
}

.btn-refresh {
  min-width: 84px;
  height: 31px;
  border: 1px solid rgba(25, 151, 198, 0.8);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.1);
  color: #1fb2e8;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  cursor: pointer;
}

.btn-refresh:hover {
  border-color: #1fb2e8;
  background: rgba(25, 151, 198, 0.2);
  color: #eaf7fc;
}

/* Table */
.table-wrap {
  overflow-x: auto;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background: rgba(9, 13, 20, 0.42);
}

.h-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.h-table th {
  padding: 10px 11px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
  color: #7f899b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.h-table td {
  padding: 10px 11px;
  border-bottom: 1px solid rgba(47, 51, 61, 0.84);
  color: #cfd2da;
  vertical-align: middle;
}

.h-table tbody tr.user-row {
  cursor: pointer;
}

.h-table tbody tr.user-row:hover {
  background: rgba(25, 151, 198, 0.075);
}

.mono { font-family: monospace; }

.user-name {
  color: #dce1eb;
  font-weight: 600;
}

.email-cell {
  display: block;
  color: #b9c2d1;
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: normal;
}

.text-cell {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.muted-sub {
  display: block;
  color: #7f899b;
  font-size: 11px;
}

.when-cell span:first-child,
.when-primary {
  display: block;
  color: #cfd2da;
}

.time-stack {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.time-row {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: baseline;
  gap: 8px;
  color: #cfd2da;
  line-height: 1.2;
}

.time-label {
  color: #7f899b;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.time-value {
  min-width: 0;
  overflow: hidden;
  color: #d8dde8;
  font-size: 11.5px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.time-row-muted .time-value {
  color: #8f98aa;
  font-weight: 600;
}

.status-pill,
.provider-pill,
.role-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 999px;
  border: 1px solid rgba(117, 124, 146, 0.3);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.status-pill.st-active {
  border-color: rgba(27, 201, 142, 0.42);
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
}

.status-pill.st-pending {
  border-color: rgba(224, 192, 126, 0.4);
  background: rgba(224, 192, 126, 0.12);
  color: #e0c07e;
}

.provider-pill.pv-google {
  border-color: rgba(25, 151, 198, 0.36);
  background: rgba(25, 151, 198, 0.1);
  color: #7bccee;
}

.provider-pill.pv-password {
  background: rgba(58, 63, 77, 0.5);
  color: #aeb5c5;
}

.provider-pill.pv-both {
  border-color: rgba(27, 201, 142, 0.4);
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
}

.role-pill.role-user {
  background: rgba(58, 63, 77, 0.5);
  color: #aeb5c5;
}

.role-pill.role-admin {
  border-color: rgba(25, 151, 198, 0.42);
  background: rgba(25, 151, 198, 0.12);
  color: #1fb2e8;
}

.role-pill.role-god {
  border-color: rgba(173, 130, 255, 0.5);
  background: rgba(123, 86, 209, 0.18);
  color: #c2acff;
}

.u-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
  min-width: 0;
  white-space: normal;
  text-align: right;
}

.row-btn {
  height: 27px;
  padding: 0 11px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(20, 25, 35, 0.7);
  color: #cfd2da;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 700;
}

.row-btn:hover:not(:disabled) {
  border-color: rgba(25, 151, 198, 0.7);
  color: #eaf7fc;
}

.row-btn.apply {
  border-color: rgba(27, 201, 142, 0.4);
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
}

.row-btn.on {
  border-color: rgba(25, 151, 198, 0.7);
  background: rgba(25, 151, 198, 0.14);
  color: #1fb2e8;
}

.row-btn.god.on {
  border-color: rgba(173, 130, 255, 0.6);
  background: rgba(123, 86, 209, 0.2);
  color: #c2acff;
}

.row-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.empty {
  padding: 18px;
  color: #6f788b;
  text-align: center;
}

/* Pager */
.pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
}

.pager-info {
  color: #8f98aa;
  font-size: 12px;
}

.pager-buttons .row-btn {
  margin-left: 8px;
  margin-bottom: 0;
}

/* Drawer */
.user-drawer {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.52);
  backdrop-filter: blur(2px);
}

.user-panel {
  width: min(520px, calc(100vw - 28px));
  height: 100%;
  padding: 22px;
  overflow-y: auto;
  border-left: 1px solid rgba(67, 72, 87, 0.9);
  background:
    radial-gradient(circle at 20% 0%, rgba(25, 151, 198, 0.14), transparent 34%),
    rgba(9, 13, 20, 0.96);
  box-shadow: -24px 0 65px rgba(0, 0, 0, 0.42);
}

.user-panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 16px;
}

.user-panel-head h5 {
  margin: 6px 0 2px;
  color: #eaf0f7;
  font-size: 17px;
  font-weight: 800;
}

.panel-close {
  width: 30px;
  height: 30px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 6px;
  background: rgba(20, 25, 35, 0.7);
  color: #cfd2da;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}

.panel-close:hover {
  border-color: rgba(25, 151, 198, 0.7);
  color: #eaf7fc;
}

.panel-loading,
.panel-error {
  margin-bottom: 12px;
  font-size: 13px;
}

.panel-loading { color: #8f98aa; }
.panel-error { color: #f0a1af; }

.user-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 18px;
}

.user-metrics > div {
  display: grid;
  gap: 4px;
  padding: 12px;
  border: 1px solid rgba(67, 72, 87, 0.86);
  border-radius: 8px;
  background: rgba(14, 18, 27, 0.7);
}

.metric-label {
  color: #8f98aa;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.user-metrics strong {
  color: #eaf0f7;
  font-size: 18px;
  font-weight: 800;
}

.metric-ok { color: #89dfbd !important; }
.metric-warn { color: #e0c07e !important; }

.user-panel-section {
  margin-bottom: 18px;
}

.section-kicker {
  display: block;
  margin-bottom: 8px;
  color: #1997c6;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.user-spec-grid {
  display: grid;
  gap: 1px;
  border: 1px solid rgba(67, 72, 87, 0.74);
  border-radius: 8px;
  overflow: hidden;
}

.user-spec-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  background: rgba(14, 18, 27, 0.6);
}

.user-spec-row span {
  color: #8f98aa;
  font-size: 12px;
}

.user-spec-row strong {
  color: #dce1eb;
  font-size: 12.5px;
  font-weight: 700;
  text-align: right;
  word-break: break-all;
}

.user-panel-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.gc-act {
  height: 36px;
  padding: 0 14px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 7px;
  background: rgba(20, 25, 35, 0.7);
  color: #d7dce8;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 700;
  transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease;
}

.gc-act:hover:not(:disabled) {
  border-color: rgba(25, 151, 198, 0.7);
  background: rgba(25, 151, 198, 0.12);
  color: #eaf7fc;
}

.gc-act.primary {
  border-color: rgba(25, 151, 198, 0.8);
  background: rgba(25, 151, 198, 0.14);
  color: #1fb2e8;
}

.gc-act:disabled {
  opacity: 0.5;
  cursor: default;
}

@media screen and (max-width: 1100px) {
  .kpi-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media screen and (max-width: 700px) {
  .registration-head,
  .registration-heading-group {
    align-items: flex-start;
    flex-direction: column;
  }

  .registration-heading-group {
    gap: 12px;
  }

  .registration-total {
    justify-items: start;
    padding-left: 0;
    border-left: 0;
  }
}
</style>
